// Voice Broadcast Report — filterable results with CSV download.
import { useEffect, useState } from "react";
import { Loader2, Download, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { fmtStamp } from "@/lib/app-utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataPagination } from "@/components/ui/data-pagination";
import { isoToday } from "./_daterange";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const PAGE_SIZE = 50;

export default function BroadcastReportPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [leadLists, setLeadLists] = useState([]);
  const [campaignId, setCampaignId] = useState("all");
  const [leadListId, setLeadListId] = useState("all");
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/broadcast/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
    api.get("/admin/resources/lead_lists").then((r) => setLeadLists(r.data || [])).catch(() => {});
  }, []);

  function qs() {
    const p = new URLSearchParams();
    if (campaignId !== "all") p.set("campaign_id", campaignId);
    if (leadListId !== "all") p.set("lead_list_id", leadListId);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    return p.toString();
  }

  async function run(toPage = 1) {
    setLoading(true);
    try {
      const r = await api.get(`/broadcast/results?${qs()}&page=${toPage}&page_size=${PAGE_SIZE}`);
      setData(r.data);
      setPage(r.data?.pagination?.page || toPage);
    }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { run(1); /* on mount */ /* eslint-disable-next-line */ }, []);

  async function downloadCsv() {
    try {
      const r = await api.get(`/broadcast/results.csv?${qs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "broadcast_results.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  }

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="br-title">
            <Megaphone className="w-5 h-5 text-primary" /> Voice Broadcast Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Per-lead outcomes and DTMF responses. Filter by broadcast, lead list, and date range.</p>
        </div>
        <Button data-testid="br-csv" onClick={downloadCsv} variant="outline" className="gap-1.5"><Download className="w-4 h-4" /> Download CSV</Button>
      </div>

      <Card className="p-4 mb-5">
        <div className="grid sm:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Broadcast</Label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger data-testid="br-campaign" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All broadcasts</SelectItem>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Lead list</Label>
            <Select value={leadListId} onValueChange={setLeadListId}>
              <SelectTrigger data-testid="br-leadlist" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All lists</SelectItem>
                {leadLists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">From</Label><Input data-testid="br-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" /></div>
          <div><Label className="text-xs">To</Label><Input data-testid="br-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" /></div>
          <Button data-testid="br-run" onClick={() => run(1)} disabled={loading} className="gap-1.5">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply filters"}</Button>
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <Stat label="Total" value={summary.total ?? 0} />
        <Stat label="Answered" value={summary.answered ?? 0} tone="good" />
        <Stat label="No answer" value={summary.no_answer ?? 0} />
        <Stat label="Machine" value={summary.machine ?? 0} />
        <Stat label="Busy" value={summary.busy ?? 0} />
        <Stat label="Transferred" value={summary.transferred ?? 0} />
        <Stat label="Opted out" value={summary.opted_out ?? 0} tone="bad" />
      </div>

      {summary.dtmf && Object.keys(summary.dtmf).length > 0 && (
        <Card className="p-4 mb-5">
          <div className="text-[10px] uppercase text-muted-foreground mb-2">DTMF responses</div>
          <div className="flex gap-2 flex-wrap" data-testid="br-dtmf-summary">
            {Object.entries(summary.dtmf).map(([d, c]) => <Badge key={d} variant="secondary" className="font-mono">Key {d}: {c}</Badge>)}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">When</th><th className="px-4 py-2">Broadcast</th><th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Outcome</th><th className="px-4 py-2">DTMF</th><th className="px-4 py-2">Dur (s)</th>
            </tr></thead>
            <tbody data-testid="br-table">
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-4 py-2 text-xs font-mono">{fmtStamp(r.created_at)}</td>
                  <td className="px-4 py-2">{r.campaign_name}</td>
                  <td className="px-4 py-2 font-mono">{r.phone}</td>
                  <td className="px-4 py-2">{r.name}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className="text-[10px] uppercase">{r.outcome}</Badge></td>
                  <td className="px-4 py-2 font-mono">{r.dtmf_digit ? `${r.dtmf_digit}${r.dtmf_label ? ` (${r.dtmf_label})` : ""}` : "—"}</td>
                  <td className="px-4 py-2">{r.duration_seconds || 0}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-muted-foreground">No results for these filters.</td></tr>}
            </tbody>
          </table>
        </div>
        <DataPagination
          page={data?.pagination?.page || page}
          pages={data?.pagination?.pages || 1}
          total={data?.pagination?.total || 0}
          pageSize={PAGE_SIZE}
          onPage={(p) => run(p)}
          testId="br-pagination"
        />
      </Card>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "good" ? "text-emerald-700 dark:text-emerald-400" : tone === "bad" ? "text-destructive" : "text-foreground";
  return (
    <Card className="p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold ${cls}`}>{value}</div>
    </Card>
  );
}
